import { Box, Chip, Typography } from "@mui/material";
import React, { useMemo } from "react";

import {
  CapabilityPartDetail,
  PartPlacement,
} from "../../types/capabilityPartDetail";
import PathTrail from "../CategoryDetail/PathTrail";
import SourceCountBlock, {
  FirstAndLatest,
} from "../CategoryDetail/SourceCountBlock";
import { entityLinkText } from "../Detail/EntityLink";
import { codeWords } from "../Detail/format";

interface PartHeadProps {
  part: CapabilityPartDetail;
}

const chipSx = { height: 24, fontSize: "0.75rem" } as const;

// Where a part stands towards the curated lists, in words
const PLACEMENT_WORDS: Record<PartPlacement, string> = {
  curated: "curated",
  filed: "found in articles, filed under a curated row",
  unlisted: "on no curated list",
};

/**
 * The head of the part view: the path above the part, its label, the labels
 * articles used, the chips for its role and its place on the curated lists,
 * and how much evidence there is and from when.
 */
const PartHead: React.FC<PartHeadProps> = ({ part }) => {
  const sourceDates = useMemo(
    () => part.sources.items.map((source) => source.date),
    [part.sources.items],
  );

  const label = entityLinkText(part.type, part.label);
  const unlisted = part.placement === "unlisted";

  return (
    <Box
      component="header"
      sx={{ px: 3, pt: 3, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <Box>
        {part.path.length > 0 && <PathTrail above={part.path} own={label} />}
        <Typography
          variant="h4"
          component="h1"
          // A part on no curated list is named in the article's own words,
          // and set apart as in the claim table
          title={unlisted ? "In the article's own words" : undefined}
          sx={{
            overflowWrap: "anywhere",
            ...(unlisted
              ? {
                  fontStyle: "italic",
                  textDecoration: "underline dotted",
                  textUnderlineOffset: "6px",
                }
              : {}),
          }}
        >
          {label}
        </Typography>
        {part.other_labels.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Labels articles used:{" "}
            <Box component="span" sx={{ color: "text.primary" }}>
              {part.other_labels.join(", ")}
            </Box>
          </Typography>
        )}
      </Box>

      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
      >
        <Chip
          label={`Capability part · ${codeWords(part.role)}`}
          variant="outlined"
          sx={chipSx}
        />
        <Chip
          label={PLACEMENT_WORDS[part.placement]}
          variant="outlined"
          sx={chipSx}
        />
      </Box>

      <SourceCountBlock
        count={part.source_count}
        dates={sourceDates}
        footnote={
          part.first_source &&
          part.latest_source && (
            <FirstAndLatest
              first={part.first_source}
              latest={part.latest_source}
            />
          )
        }
      />
    </Box>
  );
};

export default PartHead;
